/**
 * Grades API endpoints
 */

import { fetchApi, fetchRawApi, buildQueryString, withPagination } from '../core';
import type {
  GradeQueryParams,
  CreateGradeData,
  UpdateGradeData,
  CreateGradeCategoryData,
  UpdateGradeCategoryData,
  EnhancedGrade,
  GradeCategory,
} from '../types';

// Grade CRUD operations

export function getAll(params?: GradeQueryParams, componentName: string = 'Unknown') {
  const paginatedParams = withPagination(params);
  return fetchApi(`/grades${buildQueryString(paginatedParams)}`, {}, componentName);
}

export function getById(id: string, componentName: string = 'Unknown') {
  return fetchApi<EnhancedGrade>(`/grades/${id}`, {}, componentName);
}

export function create(data: CreateGradeData, componentName: string = 'Unknown') {
  return fetchApi('/grades', {
    method: 'POST',
    body: JSON.stringify(data),
  }, componentName);
}

export function update(id: string, data: UpdateGradeData, componentName: string = 'Unknown') {
  return fetchApi(`/grades/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }, componentName);
}

export function remove(id: string, componentName: string = 'Unknown') {
  return fetchApi(`/grades/${id}`, { method: 'DELETE' }, componentName);
}

// Student grade queries

export function getForStudent(studentId: string, params?: GradeQueryParams, componentName: string = 'Unknown') {
  const paginatedParams = withPagination(params);
  return fetchApi(`/students/${studentId}/grades${buildQueryString(paginatedParams)}`, {}, componentName);
}

export function getForCourseStudent(courseId: string, studentId: string, componentName: string = 'Unknown') {
  return fetchApi(`/courses/${courseId}/students/${studentId}/grades`, {}, componentName);
}

/**
 * Returns the raw gradebook CSV text for a course. The export is audited
 * server-side. Uses fetchRawApi (not fetchApi) because the response is
 * text/csv, not JSON. Mirrors the storage/ledger export pattern.
 */
export async function exportGradebookCsv(
  courseId: string,
  componentName: string = 'Unknown'
): Promise<string> {
  const response = await fetchRawApi(
    `/courses/${courseId}/gradebook/export`,
    { method: 'GET' },
    componentName
  );
  if (!response.ok) {
    let message = `Export failed (${response.status})`;
    try {
      const body = await response.json();
      if (body && typeof body.error === 'string') message = body.error;
    } catch {
      /* swallow */
    }
    throw new Error(`[${componentName}] ${message}`);
  }
  return response.text();
}

// Grade Category operations

export function getCourseCategories(courseId: string, componentName: string = 'Unknown') {
  return fetchApi<GradeCategory[]>(`/courses/${courseId}/grade-categories`, {}, componentName);
}

export function createCategory(courseId: string, data: CreateGradeCategoryData, componentName: string = 'Unknown') {
  return fetchApi(`/courses/${courseId}/grade-categories`, {
    method: 'POST',
    body: JSON.stringify(data),
  }, componentName);
}

export function updateCategory(categoryId: string, data: UpdateGradeCategoryData, componentName: string = 'Unknown') {
  return fetchApi(`/grade-categories/${categoryId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }, componentName);
}

export function deleteCategory(categoryId: string, componentName: string = 'Unknown') {
  return fetchApi(`/grade-categories/${categoryId}`, { method: 'DELETE' }, componentName);
}
